import { useMemo } from "react";

import { TooltipFormatterContextObject } from "highcharts";
import { IntlShape } from "react-intl";

import DataViewDisplayType from "@app/enum/DataViewDisplayType";
import DataViewMode from "@app/enum/DataViewMode";

import messages from "../messages";
import ChartConfig from "../type/ChartConfig";

import { COLUMN_BAR_HEIGHT } from "./const";
import styles from "./useChatConfig.module.scss";

const formatTooltip = (
  intl: IntlShape,
  name: string,
  value: number,
  grossValueFormatter: (value: number) => string,
  percentageValueFormatter: (value: number) => string,
  dataViewMode?: DataViewMode,
  totalValue?: number
) => `
  <div class="${styles.container}">
    <div>
      <b>${name}</b>
    </div>
    <br/>
    <div>
      <table>
        <tr>
          ${
            dataViewMode
              ? `<td class="${styles.firstCell}">${
                  dataViewMode === DataViewMode.SUM
                    ? intl.formatMessage(messages.tooltipTotal)
                    : intl.formatMessage(messages.tooltipAverageValue)
                }</td>`
              : ""
          }
          <td> ${grossValueFormatter ? grossValueFormatter(value) : value}</td>
        </tr>
        ${
          dataViewMode === DataViewMode.SUM && totalValue
            ? `<tr>
                <td class="${styles.firstCell}">${intl.formatMessage(
                messages.tooltipTotalPercentage
              )}</td>
                <td> ${intl.formatNumber(value / totalValue ?? 0, {
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 0,
                  style: "percent",
                })}
                </td>
              </tr>`
            : ""
        }
      </table>
    </div>
 </div>`;

const defaultValueFormatter = (value: number | undefined) => String(value ?? 0);

const useChartConfig = (config: ChartConfig): Highcharts.Options =>
  useMemo(() => {
    const {
      colors,
      columnChartHeight,
      dataDisplayType,
      dataViewMode,
      grossValueFormatter = defaultValueFormatter,
      intl,
      onBarClick,
      percentageValueFormatter = defaultValueFormatter,
      selectedPosition,
      seriesData,
      seriesName,
      totalValue,
      usePie,
    } = config;
    return usePie
      ? {
          ...(colors ? { colors } : {}),
          chart: {
            height: 300,
            type: "pie",
          },
          credits: {
            enabled: false,
          },
          plotOptions: {
            pie: {
              ...(onBarClick ? { cursor: "pointer" } : {}),
              dataLabels: {
                crop: false,
                distance: -50,
                enabled: true,
                filter: {
                  operator: ">",
                  property: "percentage",
                  value: 4,
                },
                overflow: "allow",
              },
            },
          },
          series: [
            {
              colorByPoint: true,
              data: seriesData,
              events: {
                ...(onBarClick ? { click: onBarClick } : {}),
              },
              name: seriesName,
              type: "pie",
            },
          ],
          title: {
            text: "",
          },
          tooltip: {
            formatter(this: TooltipFormatterContextObject) {
              return formatTooltip(
                intl,
                this.point.name,
                this.y ?? 0,
                grossValueFormatter,
                percentageValueFormatter,
                dataViewMode,
                totalValue
              );
            },
            useHTML: true,
          },
        }
      : {
          chart: {
            height: columnChartHeight ?? "auto",
            inverted: true,
            type: "column",
          },
          ...(colors ? { colors } : {}),
          accessibility: {
            announceNewData: {
              enabled: true,
            },
          },

          credits: {
            enabled: false,
          },
          legend: {
            enabled: false,
          },
          plotOptions: {
            series: {
              ...(onBarClick ? { cursor: "pointer" } : {}),
              dataLabels: {
                enabled: true,
                formatter: function () {
                  return dataDisplayType === DataViewDisplayType.GROSS ||
                    dataViewMode === DataViewMode.AVG
                    ? grossValueFormatter(this.y ?? 0)
                    : percentageValueFormatter(this.y ?? 0);
                },
              },
              pointPadding: 0.1,
              pointWidth: 20,
            },
          },
          series: [
            {
              colorByPoint: true,
              data: seriesData,
              events: {
                click: onBarClick,
              },
              name: seriesName,
              type: "column",
            },
          ],
          title: {
            text: "",
          },
          tooltip: {
            formatter(this: TooltipFormatterContextObject): string {
              return formatTooltip(
                intl,
                this.point.name,
                this.y ?? 0,
                grossValueFormatter,
                percentageValueFormatter,
                dataViewMode,
                totalValue
              );
            },
            outside: true,
            positioner: function (labelWidth, labelHeight, point) {
              const { left, top } = this.chart.container.getBoundingClientRect();
              return {
                x: left + labelWidth / 2,
                y: point.plotY + top - COLUMN_BAR_HEIGHT,
              };
            },
          },
          xAxis: {
            labels: {
              formatter: function () {
                return `<span class="${this.pos === selectedPosition ? styles.selectedBar : ""}">${
                  this.value
                }</span>`;
              },
            },
            lineColor: "transparent",
            lineWidth: 0,
            minorGridLineWidth: 0,
            type: "category",
          },
          yAxis: {
            gridLineWidth: 0,
            labels: {
              enabled: false,
            },
            title: {
              text: "",
            },
          },
        };
  }, [config]);

export default useChartConfig;
